import type { Core } from '@pdftron/webviewer';
import { PDFManagerFactory } from 'pdftron';
import { AnnotationCustomData, DocumentTypes } from 'types';
import { shakeDocumentsZones, zoneTypeByToolName } from './AnnotationTools/AnnotationTools';
import Crossout from './AnnotationTools/Crossout';

/**
 * Removes all annotations on the given pages from `documentType` (and their linked
 * pairs on the other document), then re-serializes and renumbers once.
 *
 * Runs the heavy work (updateInputAnnotations / shakeDocumentsZones / shakeCrossouts)
 * a single time for the whole batch. Excluding pages one at a time would repeat that
 * work per page (O(n^2)); the single-page path passes `[pageNumber]` and behaves the
 * same as excluding one page.
 */
export function excludePagesFromDocuments(pageNumbers: number[], documentType: DocumentTypes) {
  if (!pageNumbers.length) return;

  const docManager = PDFManagerFactory.getPDFDocManager();
  if (!docManager) return;

  const instance = docManager.getInstance(documentType);
  const otherInstance = docManager.getOtherInstance(instance);
  if (!instance || !otherInstance) return;

  const pagesToExclude = new Set(pageNumbers);

  // Annotations on the excluded pages of this document.
  const annotationsToExclude = instance.getAnnotationList((annot: Core.Annotations.Annotation) =>
    pagesToExclude.has(annot.PageNumber),
  );

  // Crossout numbers being removed, captured from the current numbering before deletion.
  const excludedCrossoutNumbers: number[] = annotationsToExclude
    .filter((annot) => annot.getCustomData(AnnotationCustomData.strikeoutAnnotation) === 'true')
    .map((crossoutAnnot) => parseInt(crossoutAnnot.getCustomData(AnnotationCustomData.zoneNumber), 10));

  // Zones come in source/target pairs, so delete the matching annotations on the other document.
  const otherInstanceAnnotations = otherInstance.getAnnotationList();
  annotationsToExclude.forEach((excludedAnnotation) => {
    if (zoneTypeByToolName[excludedAnnotation.ToolName] === undefined) return;
    // Target zone annotation ids carry extra suffixes; the shared part is before the first underscore.
    const excludedAnnotationId =
      documentType === DocumentTypes.target ? excludedAnnotation.Id.split('_')[0] : excludedAnnotation.Id;
    const matchingAnnotations = otherInstanceAnnotations.filter((otherInstanceAnnotation) =>
      otherInstanceAnnotation.Id.includes(excludedAnnotationId),
    );
    if (matchingAnnotations.length) {
      otherInstance.annotationManager.deleteAnnotations(matchingAnnotations, { imported: false, force: true });
    }
  });

  instance.annotationManager.deleteAnnotations(annotationsToExclude, { imported: false, force: true });

  // Re-serialize and renumber once for the whole batch.
  docManager.updateInputAnnotations();
  shakeDocumentsZones();
  Crossout.shakeCrossouts(excludedCrossoutNumbers);
}
